import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Upload, Download, File } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function DocumentsPage() {
  const { axiosAuth } = useAuth();
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('general');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const api = axiosAuth();

  const load = () => api.get('/documents').then(r => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleUpload = async () => {
    if (!file) return toast.error('Please select a file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('category', category);
      await api.post('/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded!');
      setOpen(false);
      setTitle('');
      setCategory('general');
      setFile(null);
      load();
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || doc.title || 'file';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const catColors = { court: 'bg-slate-50 text-slate-700', treatment: 'bg-teal-50 text-teal-700', personal: 'bg-sky-50 text-sky-700', general: 'bg-muted text-muted-foreground' };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Documents</h1><p className="text-muted-foreground text-sm mt-1">Upload and manage important documents</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="upload-document-button"><Upload size={16} /> Upload</Button></DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Upload Document</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-teal-300 hover:bg-teal-50/30 transition-colors"
                data-testid="file-drop-zone"
              >
                <File className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                {file ? (
                  <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">{formatSize(file.size)}</p></div>
                ) : (
                  <p className="text-sm text-muted-foreground">Click to select a file (PDF, images, documents)</p>
                )}
                <input ref={fileRef} type="file" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt" />
              </div>
              <div className="space-y-1.5"><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title" data-testid="doc-title-input" /></div>
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="court">Court Papers</SelectItem><SelectItem value="treatment">Treatment Plans</SelectItem><SelectItem value="personal">Personal</SelectItem><SelectItem value="general">General</SelectItem></SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpload} disabled={uploading || !file} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-document-button">
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {docs.length > 0 ? docs.map(d => (
          <Card key={d.id} className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-teal-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{d.title || d.filename}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className={catColors[d.category] || 'bg-muted'} variant="secondary">{d.category}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDate(d.uploaded_at)}</span>
                  {d.file_size && <span className="text-xs text-muted-foreground">{formatSize(d.file_size)}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">{d.status}</Badge>
                {d.file_path && (
                  <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" onClick={() => handleDownload(d)} data-testid={`download-doc-${d.id}`}>
                    <Download size={14} /> Download
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No documents yet.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
